import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  debounce,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { NumberFormatCustom } from "../components/NumberFormat";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { UserContext } from "../contexts/UserContext";
import { useQueryParam } from "use-query-params";
import Iconify from "../../minimals/components/iconify";
import { prependUserToSelectNote } from "../utils/prependUserToSelectNote";
import { stripBase64LeadingChars, toBase64 } from "../utils/helpers";
import { UploadMultiFile } from "../components/upload";
import { checkIfLedgerExists } from "../shared/requests/ledgers";

type LedgerTransaction = {
  id: string;
  status: string;
  amount: number;
  name: string;
  payorPayee: string;
  type: string;
  typeDescription: string;
  isDebit: boolean;
  referenceNumber: string;
  memo: string | null;
  systemPostedOn: string | null;
  userPostedOn: string | null;
  voidedOn: string | null;
  clearedOn: string | null;
  transactionDate: string | null;
};

type OrderLedgerBalanceResponse = {
  ledgerBalance: number;
  trustAccount?: {
    code?: string | null;
  } | null;
};

const INTEREST_REQUIRED_TRUST_ACCOUNTS = [
  "BU-CTAVA",
  "BU-EPT",
  "BU-FL4726",
  "BU-MD5557",
  "BU-MMAMD",
  "BU-MMAVA",
  "BU-OH7843",
  "BU-TX6726",
  "BU-VA3173",
  "BU-WTRFL",
  "BU-WTRMD",
  "BU-WTROH",
  "BU-WTRVA",
  "CB-CTAMD",
  "CB-MMAMD",
  "CB-MTAFL",
  "CB-MTAKS",
  "CB-MTAMD",
  "CB-WTRMD",
  "CTAMD-CO",
  "FB-MTAFL",
  "MMAMD-CO",
  "MMAVA-CO",
  "MMTMD-CO",
  "MMTVA-CO",
  "MTAFL-CITI",
  "MTAMD-CO",
  "MTAOH-CF",
  "MTAOH-CUST",
  "MTAOH-HUNT",
  "MTAOH-PNC",
  "MTATX-B1",
  "MTATX-CO",
  "MTAVA-CO",
  "NYCB-FL",
  "NYCB-OH",
  "OH-REC",
  "WALMD-CO",
  "WALVA-CO",
  "WTRFL-CITI",
  "WTRMD-CO",
  "WTROH-PNC",
  "WTRVA-CO"
];

export default function ClosingChecklistEscrow({ onClose, refresh }: { onClose: () => void; refresh: () => void }) {
  const { user } = useContext(UserContext);
  const [existingOrderNumber] = useQueryParam<string | undefined>("order");
  const [submitting, setSubmitting] = useState(false);
  const [amount, setAmount] = useState<string | null>(null);
  const [memo, setMemo] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [orderNumber, setOrderNumber] = useState<string | null>(existingOrderNumber || null);
  const [notes, setNotes] = useState<string | null>(null);
  const [interestRequired, setInterestRequired] = useState<boolean | null>(null);
  const [isInterestRequiredSelectionRequired, setIsInterestRequiredSelectionRequired] = useState(false);
  const [assignToEscrowTeam, setAssignToEscrowTeam] = useState(true);
  const [ledgerBalance, setLedgerBalance] = useState(0);
  const [orderNumbers, setOrderNumbers] = useState<string[]>([]);
  const [loadingOrderNumbers, setLoadingOrderNumbers] = useState<boolean>(false);
  const [transactions, setTransactions] = useState<LedgerTransaction[]>([]);
  const [existingTransactionId, setExistingTransactionId] = useState("");
  const [touched, setTouched] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const handleFileDrop = useCallback(
    (acceptedFile: any) => {
      const file = Object.assign(acceptedFile[0], {
        preview: URL.createObjectURL(acceptedFile[0])
      });
      setFile(file);
    },
    [setFile]
  );

  const uploadFiles = async () => {
    if (!file) return;
    const escrowFiles = [
      {
        name: file.name,
        base64Contents: stripBase64LeadingChars(await toBase64(file as File)),
        description: `Escrow Agreement for ${memo}`
      }
    ];
    await axios.post("/api/file/uploadfiles", { orderNumber, escrowFiles });
  };

  const sendInterestBearingSubAccountEmail = async () => {
    if (!orderNumber || !isInterestRequiredSelectionRequired || !interestRequired) return;

    await axios.post("/api/escrowinterest/sendInterestBearingSubAccountEmail", { orderNumber });
  };

  const submitTransfer = async () => {
    setTouched(true);
    if (!amount || !memo || !dueDate || (isInterestRequiredSelectionRequired && interestRequired === null)) return;
    setSubmitting(true);
    await axios.post("/proxy/api/ledgers/CreateEscrowLedger", {
      orderNumber: orderNumber,
      amount: parseFloat(amount),
      for: memo,
      dueDate,
      userName: user?.username,
      notes: notes ? prependUserToSelectNote(notes, user?.name as string) : null,
      assignToEscrowTeam,
      transactionId: existingTransactionId,
      interestRequired
    });
    await sendInterestBearingSubAccountEmail();
    if (file) {
      await uploadFiles();
    }
    refresh();
    onClose();
  };

  const getLedgerBalance = async (orderNumber: string | null) => {
    if (!orderNumber) {
      setLedgerBalance(0);
      setIsInterestRequiredSelectionRequired(false);
      setInterestRequired(null);
      return;
    }

    const isLedger = await checkIfLedgerExists(orderNumber);
    if (!isLedger) {
      setLedgerBalance(0);
      setIsInterestRequiredSelectionRequired(false);
      setInterestRequired(null);
      return;
    }

    const { data } = await axios.get<OrderLedgerBalanceResponse>(
      `/proxy/api/ledgers/GetOrderAndLedgerBalance?ordernumber=${orderNumber}`
    );
    setLedgerBalance(data.ledgerBalance);
    setIsInterestRequiredSelectionRequired(
      INTEREST_REQUIRED_TRUST_ACCOUNTS.includes(data.trustAccount?.code?.toUpperCase() ?? "")
    );
    setInterestRequired(null);
  };

  const loadTransactions = async (orderNumber: string) => {
    const response = await axios.get<LedgerTransaction[]>(
      `/proxy/api/ledgers/gettransactions?orderNumber=${orderNumber}`
    );
    const filteredTransactions = response.data.filter((transaction) => {
      return transaction.typeDescription.startsWith("Ledger Transfer") && transaction.status === "Pending";
    });
    setTransactions(filteredTransactions);
  };

  const debouncedOrderSearchTextChange = debounce(async (text: string | null) => {
    if (!text) {
      setOrderNumbers([]);
      return;
    }
    setLoadingOrderNumbers(true);
    const { data } = await axios.get(`/proxy/api/orders/FindRelatedOrdersByNumber?searchText=${text}`);
    setOrderNumbers(data);
    setLoadingOrderNumbers(false);
  }, 500);

  useEffect(() => {
    getLedgerBalance(orderNumber);
  }, [orderNumber]);

  useEffect(() => {
    if (orderNumber) {
      loadTransactions(orderNumber);
    }
  }, []);

  const isValidTransactionAmount = parseFloat(amount!) <= ledgerBalance;

  return (
    <Dialog open={true} maxWidth="sm" fullWidth onClose={onClose}>
      <DialogTitle>Transfer Funds to Escrow</DialogTitle>
      <DialogContent sx={{ paddingBottom: 4 }}>
        <Stack direction="column" marginTop={1} spacing={2}>
          {!existingOrderNumber && (
            <Autocomplete
              options={orderNumbers}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Order Number"
                  onChange={(e) => debouncedOrderSearchTextChange(e.target.value)}
                />
              )}
              onChange={async (e, val) => {
                setOrderNumber(val);
                if (val) {
                  await loadTransactions(val!);
                }
              }}
              loading={loadingOrderNumbers}
            />
          )}
          <TextField
            select
            fullWidth
            disabled={!orderNumber}
            label="Choose Pending Transaction"
            value={existingTransactionId}
            onChange={(e) => {
              const transaction = transactions.find((t) => t.id === e.target.value);
              if (transaction) {
                setAmount(transaction?.amount.toString());
                setExistingTransactionId(transaction.id);
              }
            }}
            variant="outlined"
            InputProps={{
              endAdornment: existingTransactionId && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      setExistingTransactionId("");
                      setAmount(null);
                    }}
                    size="small"
                  >
                    <Iconify icon="material-symbols:close-small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          >
            {transactions.map((transaction) => (
              <MenuItem key={transaction!.id} value={transaction!.id}>
                {transaction!.name} - ${transaction!.amount.toFixed(2)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            error={touched && !amount}
            fullWidth
            label="Amount"
            value={amount}
            variant="outlined"
            onChange={(e) => setAmount(e.target.value)}
            InputProps={{
              inputComponent: NumberFormatCustom as any
            }}
            disabled={!orderNumber || Boolean(existingTransactionId)}
          />

          <TextField
            required
            error={touched && !memo}
            fullWidth
            label="For"
            variant="outlined"
            onChange={(e) => setMemo(e.target.value)}
            disabled={!orderNumber}
            inputProps={{ maxLength: 50 }}
            helperText={memo?.length === 50 ? "Only 50 characters allowed, use notes for additional info." : null}
          />
          <DatePicker
            label="Due Date"
            onChange={(value) => setDueDate(value ? (dayjs.isDayjs(value) ? value.toDate() : value) : null)}
            disabled={!orderNumber}
            slotProps={{
              textField: {
                required: true,
                error: touched && !dueDate
              }
            }}
          />
          <FormControl
            required={isInterestRequiredSelectionRequired}
            error={touched && isInterestRequiredSelectionRequired && interestRequired === null}
            disabled={!orderNumber}
          >
            <FormLabel>Interest Required</FormLabel>
            <RadioGroup
              row
              value={interestRequired === null ? "" : interestRequired ? "yes" : "no"}
              onChange={(e) => setInterestRequired(e.target.value === "yes")}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
            {touched && isInterestRequiredSelectionRequired && interestRequired === null && (
              <FormHelperText>Select Yes or No.</FormHelperText>
            )}
            {isInterestRequiredSelectionRequired && interestRequired && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Please note that a W9 is required to be obtained from the party earning interest to open up an
                interest-bearing sub-account. Please reach out to the relevant party asking for a W9, and save to web a
                copy of your request for the W9. Once the W9 is received, forward it to Accounting and specify the escrow
                funds must be placed in an interest-bearing sub-account.
              </Alert>
            )}
          </FormControl>
          <TextField
            fullWidth
            label="Notes"
            variant="outlined"
            multiline
            rows={4}
            onChange={(e) => setNotes(e.target.value)}
            disabled={!orderNumber}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={assignToEscrowTeam}
                onChange={(e) => setAssignToEscrowTeam(e.target.checked)}
                disabled={!orderNumber}
              />
            }
            label="Assign to Escrow Team"
          />

          <UploadMultiFile
            files={file ? [file] : []}
            onDrop={handleFileDrop}
            onRemove={() => setFile(null)}
            onRemoveAll={() => setFile(null)}
            showPreview={false}
            showSelectedFiles={true}
            multiple={false}
            sx={{ marginTop: 3 }}
          />

          {file && (
            <Typography marginTop={3}>
              Uploaded document will be saved to
              <br /> Post Closing {">"} Escrow {">"} {`Escrow Agreement for ${memo}`}
            </Typography>
          )}

          {!isValidTransactionAmount && (
            <Typography marginTop={3} marginBottom={3} sx={{ color: "error.main" }}>
              There are insufficient funds for this transaction.
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={1} marginTop={4} justifyContent="flex-end">
          <Button color="error" variant="contained" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitTransfer} disabled={!isValidTransactionAmount || !orderNumber}>
            {submitting ? (
              <>
                Transferring <CircularProgress size={16} color="inherit" sx={{ marginLeft: 1 }} />
              </>
            ) : (
              "Transfer"
            )}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
